import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CloudUploadOutlined,
  DownloadOutlined,
  ExpandLess,
  ExpandMore,
  ImageNotSupportedOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import ImageGalleryPopup from "../../../../../views/components/common/pop-up/ImageGalleryPopup";
import EyeIcon from "../../../../../assets/eyeframe.png";
import type { FilterValue } from "../../../../../models/Filters/FilterValue";
import type { PSDTemplate } from "../../../../../models/MockupConfig/index";
import { useStores } from "../../../use-stores";

interface PodProductPsdMappingsCardProps {
  productId: number;
  skuChoices?: string[];
  loadingOptions?: boolean;
  uploading?: boolean;
  onUploadPsd: () => void | Promise<void>;
  onSetDefault: (templateId: number) => void | Promise<void>;
  onRemove: (templateId: number) => void | Promise<void>;
  onDownload: (templateId: number) => void;
  embedded?: boolean;
}

const SECTION_PAPER_SX = {
  p: { xs: 2, sm: 2.5 },
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "none",
  bgcolor: "background.paper",
} as const;

function StepBadge({ step }: { step: number }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 28,
        height: 28,
        borderRadius: 1.25,
        bgcolor: "primary.main",
        color: "primary.contrastText",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.8rem",
        fontWeight: 800,
        flexShrink: 0,
      }}
    >
      {step}
    </Box>
  );
}

function PsdTemplateCard({
  psd,
  isDefault,
  busy,
  hovered,
  onMouseEnter,
  onMouseLeave,
  onSetDefault,
  onRemove,
  onDownload,
  onPreview,
}: {
  psd: PSDTemplate;
  isDefault: boolean;
  busy: boolean;
  hovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onSetDefault: () => void;
  onRemove: () => void;
  onDownload: () => void;
  onPreview: () => void;
}) {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const previewSrc = psd.preview_url || psd.blank_preview_url;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        width: 200,
        borderRadius: 1.5,
        borderColor: isDefault ? colors.purpleMain : colors.greyBackground,
        backgroundColor: colors.white,
        transition: "border-color 0.2s",
      }}
    >
      <Box
        sx={{ position: "relative" }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {previewSrc ? (
          <Box
            component="img"
            src={previewSrc}
            alt={`${psd.placement_type_name ?? "PSD"} preview`}
            sx={{
              width: "100%",
              height: 180,
              objectFit: "cover",
              borderRadius: 1,
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 180,
              borderRadius: 1,
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ImageNotSupportedOutlined
              sx={{ fontSize: 40, color: "text.disabled" }}
            />
          </Box>
        )}

        {hovered && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Tooltip title="Download PSD">
              <IconButton
                size="small"
                onClick={onDownload}
                disabled={busy}
                sx={{ color: colors.white }}
              >
                <DownloadOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Preview">
              <IconButton
                size="small"
                onClick={onPreview}
                sx={{ color: colors.white }}
              >
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {isDefault && (
          <Chip
            label="Default"
            size="small"
            color="primary"
            sx={{
              position: "absolute",
              top: 6,
              left: 6,
              height: 20,
              fontSize: "0.65rem",
              borderRadius: 1,
              fontWeight: 700,
            }}
          />
        )}
      </Box>

      <Box mt={0.75} px={0.5}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{ fontSize: "0.8rem" }}
        >
          {psd.placement_type_name || "Unknown placement"}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {psd.variant_sku?.trim() ? psd.variant_sku : "Product"}
        </Typography>
        {psd.created_by_admin_name || psd.updated_by_admin_name ? (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {psd.updated_by_admin_name &&
            psd.updated_by_admin_name !== psd.created_by_admin_name
              ? `Updated by ${psd.updated_by_admin_name}`
              : `Uploaded by ${psd.created_by_admin_name || psd.updated_by_admin_name}`}
          </Typography>
        ) : null}
      </Box>

      <Box mt={0.5} display="flex" justifyContent="space-around" gap={1}>
        <Grid2 container columns={12}>
          <AppButton
            variant="transparentPrimary"
            label={isDefault ? "Default" : "Set default"}
            onClick={onSetDefault}
            disabled={busy || isDefault}
            customStyles={{ fontSize: 11, minHeight: 28 }}
          />
        </Grid2>
        <Grid2 container columns={12}>
          <AppButton
            variant="cancel"
            label="Remove"
            onClick={onRemove}
            disabled={busy}
            customStyles={{ fontSize: 11, minHeight: 28 }}
          />
        </Grid2>
      </Box>
    </Paper>
  );
}

const PodProductPsdMappingsCard: React.FC<PodProductPsdMappingsCardProps> = ({
  productId,
  skuChoices = [],
  loadingOptions = false,
  uploading = false,
  onUploadPsd,
  onSetDefault,
  onRemove,
  onDownload,
  embedded = false,
}) => {
  const theme = useTheme();
  const { podCatalogStore } = useStores();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(true);
  const [busyTemplateId, setBusyTemplateId] = useState<number | null>(null);
  const [hoveredCardKey, setHoveredCardKey] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const placementSelected = Boolean(podCatalogStore.detailPsdPlacementTypeId);
  const skuSelected = Boolean(podCatalogStore.detailPsdVariantSku.trim());
  const fileSelected = Boolean(podCatalogStore.detailPsdFile);
  const canChooseFile =
    placementSelected && skuSelected && !loadingOptions && !uploading;
  const canUpload =
    placementSelected && skuSelected && !loadingOptions && !uploading;

  const selectedPlacementId = Number(podCatalogStore.detailPsdPlacementTypeId);
  const selectedSku = podCatalogStore.detailPsdVariantSku.trim().toUpperCase();
  const existingForPlacement = selectedSku
    ? podCatalogStore.detailPsdTemplates.find(
        (psd) =>
          psd.placement_type_id === selectedPlacementId &&
          String(psd.variant_sku ?? "").trim().toUpperCase() === selectedSku,
      )
    : undefined;

  const placementTypeOptions: FilterValue[] = useMemo(
    () => [
      { id: "", value: "Select placement (e.g. Front, Back)" },
      ...podCatalogStore.detailPsdPlacementTypes.map((type) => ({
        id: String(type.id),
        value: type.name,
      })),
    ],
    [podCatalogStore.detailPsdPlacementTypes],
  );

  const skuOptions: FilterValue[] = useMemo(() => {
    const seen = new Set<string>();
    const choices: FilterValue[] = [{ id: "", value: "Select SKU" }];
    for (const sku of skuChoices) {
      const value = sku.trim();
      const key = value.toUpperCase();
      if (!value || seen.has(key)) continue;
      seen.add(key);
      choices.push({ id: value, value });
    }
    return choices;
  }, [skuChoices]);

  const uploadedCount = podCatalogStore.detailPsdTemplates.length;

  const galleryImages = useMemo(
    () =>
      podCatalogStore.detailPsdTemplates
        .filter((t) => t.preview_url || t.blank_preview_url)
        .map((t) => ({
          id: t.id,
          src: (t.preview_url || t.blank_preview_url) as string,
          alt: t.placement_type_name || "PSD preview",
        })),
    [podCatalogStore.detailPsdTemplates],
  );

  const openGalleryForTemplate = (psd: PSDTemplate) => {
    const src = psd.preview_url || psd.blank_preview_url;
    if (!src) return;
    const idx = galleryImages.findIndex((img) => img.src === src);
    setGalleryIndex(idx >= 0 ? idx : 0);
    setGalleryOpen(true);
  };

  const handleSetDefault = async (templateId: number) => {
    try {
      setBusyTemplateId(templateId);
      await onSetDefault(templateId);
    } finally {
      setBusyTemplateId(null);
    }
  };

  const handleRemove = async (templateId: number) => {
    try {
      setBusyTemplateId(templateId);
      await onRemove(templateId);
    } finally {
      setBusyTemplateId(null);
    }
  };

  const content = (
    <>
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        <Typography variant="body2" component="div">
          <strong>How it works:</strong> (1) Select placement. (2) Select a
          SKU. New PSDs are saved against that SKU only. Older product PSDs
          stay and are not used for another color. (3) Upload. Re-uploading
          the same placement and SKU replaces that file. Locally the saved
          preview is a fixed image, so a PSD file is optional.
        </Typography>
      </Alert>

      <Paper sx={SECTION_PAPER_SX}>
        <Grid2 container spacing={{ xs: 2.5, lg: 3 }} alignItems="stretch">
          <Grid2 size={{ xs: 12, lg: 6 }}>
            <Stack spacing={2} height="100%">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <StepBadge step={1} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Placement
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Required before you can attach a file
                  </Typography>
                </Box>
              </Stack>
              <AppSearchDropdownMulti
                title="Placement type"
                fontSize="0.8rem"
                isMultiSelect={false}
                list={placementTypeOptions}
                value={[
                  {
                    id: podCatalogStore.detailPsdPlacementTypeId,
                    value: podCatalogStore.detailPsdPlacementTypeId,
                  },
                ]}
                local={true}
                handleSelect={(values: FilterValue[]) => {
                  podCatalogStore.setDetailPsdPlacementTypeId(
                    values[0]?.id || "",
                  );
                }}
                disabled={loadingOptions || uploading}
              />
              <AppSearchDropdownMulti
                title="SKU"
                fontSize="0.8rem"
                isMultiSelect={false}
                list={skuOptions}
                value={[
                  {
                    id: podCatalogStore.detailPsdVariantSku,
                    value: podCatalogStore.detailPsdVariantSku || "Select SKU",
                  },
                ]}
                local={true}
                handleSelect={(values: FilterValue[]) => {
                  podCatalogStore.setDetailPsdVariantSku(values[0]?.id || "");
                }}
                disabled={loadingOptions || uploading}
              />
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12, lg: 6 }}>
            <Stack spacing={2} height="100%">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <StepBadge step={2} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    PSD file
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {placementSelected
                      ? "Select a Photoshop (.psd) file for this placement."
                      : "Select a placement first to enable file upload."}
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px dashed",
                  borderColor: canChooseFile ? "primary.light" : "divider",
                  borderRadius: 2,
                  bgcolor: canChooseFile ? "action.hover" : "grey.50",
                  opacity: canChooseFile ? 1 : 0.65,
                  px: 2,
                  py: 2.5,
                  textAlign: "center",
                  minHeight: { xs: 140, lg: 168 },
                  transition: "border-color 0.2s, opacity 0.2s",
                }}
              >
                <CloudUploadOutlined
                  sx={{
                    fontSize: 36,
                    color: canChooseFile ? "primary.main" : "text.disabled",
                    mb: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  {fileSelected
                    ? podCatalogStore.detailPsdFile?.name
                    : "Adobe Photoshop (.psd) only"}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  Max file size: 5 MB only.
                </Typography>
                <AppButton
                  label={fileSelected ? "Change file" : "Choose PSD file"}
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!canChooseFile}
                  customStyles={{ minWidth: 160 }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".psd"
                  hidden
                  disabled={!canChooseFile}
                  onChange={(e) => {
                    podCatalogStore.setDetailPsdFile(
                      e.target.files?.[0] || null,
                    );
                    e.currentTarget.value = "";
                  }}
                />
              </Box>
            </Stack>
          </Grid2>
        </Grid2>

        {existingForPlacement && placementSelected && (
          <Alert severity="warning" sx={{ borderRadius: 1.5, mt: 2.5 }}>
            A PSD is already uploaded for{" "}
            <strong>{existingForPlacement.placement_type_name}</strong>
            {" "}on{" "}
            <strong>
              {existingForPlacement.variant_sku?.trim() || "Product"}
            </strong>
            . Uploading again will replace it.
          </Alert>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Box display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1.5}>
          <AppButton
            label={uploading ? "Uploading…" : "Upload PSD"}
            variant="primary"
            onClick={onUploadPsd}
            disabled={!canUpload}
            customStyles={{
              minWidth: 160,
              background: canUpload
                ? `linear-gradient(90deg, ${theme.palette.customColors.purpleGradientStart} 0%, ${theme.palette.customColors.purpleGradientEnd} 100%)`
                : undefined,
              color: canUpload ? theme.palette.customColors.white : undefined,
            }}
          />
        </Box>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 2 }}
        >
          <CustomTypography variant="heading8">
            Uploaded PSDs ({uploadedCount})
          </CustomTypography>
        </Stack>

        {loadingOptions && uploadedCount === 0 ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading placements and uploaded files…
            </Typography>
          </Box>
        ) : uploadedCount === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              py: 4,
              px: 2,
              textAlign: "center",
              borderRadius: 2,
              borderStyle: "dashed",
            }}
          >
            <ImageNotSupportedOutlined
              sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              No PSDs uploaded for this product yet.
            </Typography>
          </Paper>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={1.5}>
            {podCatalogStore.detailPsdTemplates.map((psd) => {
              const cardKey =
                psd.id != null
                  ? String(psd.id)
                  : `${psd.placement_type_id}-${psd.reference_id ?? "psd"}`;
              return (
                <PsdTemplateCard
                  key={cardKey}
                  psd={psd}
                  isDefault={Boolean(psd.is_default)}
                  busy={busyTemplateId === psd.id}
                  hovered={hoveredCardKey === cardKey}
                  onMouseEnter={() => setHoveredCardKey(cardKey)}
                  onMouseLeave={() => setHoveredCardKey(null)}
                  onSetDefault={() => {
                    if (psd.id != null) void handleSetDefault(psd.id);
                  }}
                  onRemove={() => {
                    if (psd.id != null) void handleRemove(psd.id);
                  }}
                  onDownload={() => {
                    if (psd.id != null) onDownload(psd.id);
                  }}
                  onPreview={() => openGalleryForTemplate(psd)}
                />
              );
            })}
          </Box>
        )}
      </Box>

      <ImageGalleryPopup
        open={galleryOpen}
        images={galleryImages}
        title="PSD Previews"
        initialIndex={galleryIndex}
        onClose={() => setGalleryOpen(false)}
      />
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card sx={{ marginBottom: 2 }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <img src={EyeIcon} alt="" style={{ width: 30, height: 30 }} />
            <CustomTypography variant="heading5">
              PSD Configuration
            </CustomTypography>
            {uploadedCount > 0 && (
              <Chip
                label={`${uploadedCount} uploaded`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ borderRadius: 2, height: 22, fontSize: "0.7rem" }}
              />
            )}
          </Box>
        }
        subheader={
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, maxWidth: 560, fontSize: "12px" }}
          >
            Mockup templates for this product. Choose where the design sits on
            the product, then upload one PSD per placement.
          </Typography>
        }
        action={
          <IconButton
            aria-label={open ? "Collapse PSD section" : "Expand PSD section"}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((expanded) => !expanded);
            }}
          >
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
        sx={{
          cursor: "pointer",
          px: 3,
          py: 2,
          borderBottom: open
            ? `1px solid ${theme.palette.customColors?.lightBorder ?? theme.palette.divider}`
            : "none",
          "&:hover": { bgcolor: "action.hover" },
        }}
        onClick={() => setOpen((expanded) => !expanded)}
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <CardContent sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 3, pb: 3 }}>
          {content}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default observer(PodProductPsdMappingsCard);
